import { Fragment, useEffect, useId, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLine } from '../aiAccess/shared';
import type { Batch, BulkRow, BulkRows, EnrichmentClient, ImportResponse } from './api';
import {
  BULK_CONCURRENCY,
  PROGRESS_EVERY,
  SECONDS_PER_LOOKUP,
  blockedCsv,
  csvName,
  downloadCsv,
  finishCounts,
  finishOutcome,
  minutesLeft,
  progressCounts,
  resultsCsv,
  runBulkLookup,
  sha256Hex,
  tally,
  type RowResult,
} from './bulk';
import { OUTCOME_BADGES, OEM_NAMES, countsText, oemName, serialCount, type Tone } from './format';
import { Badge, Icon, IconTile, LABEL, LINK, MONO, MUTED, Ring, SrOnly, TEXT, TONE_TEXT, buttonClass, useDialog } from './ui';

export type UploadMode = 'lookup' | 'import';

/** Rows of the file shown on the review step, and the latest rows shown while it runs. */
const FIRST_ROWS = 4;
const LATEST_ROWS = 5;
const INVALID_SHOWN = 50;
const IMPORT_RESULTS_SHOWN = 200;

interface Run {
  phase: 'starting' | 'running' | 'finishing' | 'done';
  batch: Batch | null;
  /** By row index; null until answered. */
  results: Array<RowResult | null>;
  /** Rows started, in file order. */
  started: number;
  cancelled: boolean;
  finishError: string | null;
}

const IMPORT_BADGES: Record<string, { label: string; tone: Tone }> = {
  success: { label: 'Found', tone: 'ok' },
  not_found: { label: 'Not found', tone: 'neutral' },
  oem_unknown: { label: 'OEM unknown', tone: 'neutral' },
  unsupported: { label: 'Unsupported', tone: 'neutral' },
  needs_more_info: { label: 'Needs model no.', tone: 'warn' },
  error: { label: 'Failed', tone: 'bad' },
  failed: { label: 'Failed', tone: 'bad' },
  skipped: { label: 'Skipped', tone: 'neutral' },
};

/**
 * DOC 4–5, "Upload a list": look a customer's list up (choose the file, review what the service
 * read from it, then look its rows up three at a time in one batch, sending the counts every
 * PROGRESS_EVERY rows and finishing the batch at the end), or import answers enriched elsewhere.
 * While a list runs, Minimise folds the dialog into a small pill (`minimised`) and leaving the page
 * asks first; closing the dialog for good is the caller's (`onClose`), which unmounts it.
 */
export function UploadModal({ client, initialMode, minimised, onMinimise, onRestore, onClose, onChanged, fallbackFocus }: {
  readonly client: EnrichmentClient | null;
  readonly initialMode: UploadMode;
  readonly minimised: boolean;
  readonly onMinimise: () => void;
  readonly onRestore: () => void;
  readonly onClose: () => void;
  /** A batch started or finished, or answers were imported: lists and counts load again. */
  readonly onChanged: () => void;
  readonly fallbackFocus?: RefObject<HTMLElement>;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<UploadMode>(initialMode);
  // Look up a list.
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<BulkRows | null>(null);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const [run, setRun] = useState<Run | null>(null);
  const runRef = useRef<AbortController | null>(null);
  const readRef = useRef<AbortController | null>(null);
  // Import answers.
  const [answers, setAnswers] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState<ImportResponse | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const dialogRef = useRef<HTMLDivElement>(null);
  const headingId = useId();
  const active = run !== null && run.phase !== 'done';
  const close = () => {
    if (active) onMinimise();
    else if (!importing && !reading) onClose();
  };
  const { onKeyDown } = useDialog(dialogRef, close, !minimised, fallbackFocus);

  // Leaving the page stops a run (its batch still finishes, as cancelled); a reload or a closed
  // tab asks first.
  useEffect(() => () => {
    runRef.current?.abort();
    readRef.current?.abort();
  }, []);
  useEffect(() => {
    if (!active) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Older browsers show the prompt only when returnValue is set.
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [active]);

  const read = async (next: File) => {
    if (!client) return;
    readRef.current?.abort();
    const request = new AbortController();
    readRef.current = request;
    setFile(next);
    setParsed(null);
    setReadError(null);
    setReading(true);
    try {
      const rows = await client.bulkRows(next, request.signal);
      if (!request.signal.aborted) setParsed(rows);
    } catch (e) {
      if (!request.signal.aborted) setReadError((e as Error).message || t('Something went wrong.'));
    } finally {
      if (!request.signal.aborted) setReading(false);
    }
  };

  const start = async () => {
    const rows = parsed?.rows ?? [];
    if (!client || !file || rows.length === 0) return;
    const controller = new AbortController();
    runRef.current = controller;
    setReadError(null);
    setRun({ phase: 'starting', batch: null, results: rows.map(() => null), started: 0, cancelled: false, finishError: null });
    let batch: Batch;
    try {
      batch = await client.startBatch({ kind: 'bulk_lookup', file_name: file.name, file_sha256: await sha256Hex(file), row_count: rows.length });
      if (!batch?.id) throw new Error(t('The service did not return a batch.'));
    } catch (e) {
      setRun(null);
      setReadError((e as Error).message || t('Something went wrong.'));
      return;
    }
    const batchId = batch.id as string;
    setRun((current) => current && { ...current, phase: 'running', batch });
    onChanged();
    const answered: Array<RowResult | null> = rows.map(() => null);
    let done = 0;
    const outcome = await runBulkLookup(
      rows,
      (row, signal) => client.lookup({ serial_number: row.serial_number, oem: row.oem, batch_id: batchId }, signal),
      {
        signal: controller.signal,
        onStart: () => setRun((current) => current && { ...current, started: current.started + 1 }),
        onResult: (index, result) => {
          answered[index] = result;
          done += 1;
          setRun((current) => {
            if (!current) return current;
            const results = [...current.results];
            results[index] = result;
            return { ...current, results };
          });
          // Other admins see the batch move on the uploads list; the last rows go with finish.
          if (done % PROGRESS_EVERY === 0 && done < rows.length) {
            client.batchProgress(batchId, progressCounts(answered)).catch(() => undefined);
          }
        },
      },
    );
    setRun((current) => current && { ...current, phase: 'finishing', results: outcome.results, cancelled: outcome.cancelled });
    let finished: Batch | null = null;
    let finishError: string | null = null;
    try {
      finished = await client.finishBatch(batchId, { outcome: finishOutcome(outcome), counts: finishCounts(outcome.results) });
    } catch (e) {
      finishError = t('The batch could not be closed: {{reason}}', { reason: (e as Error).message });
    }
    setRun((current) => current && { ...current, phase: 'done', batch: finished ?? current.batch, finishError });
    onChanged();
  };

  const importFile = async () => {
    if (!client || !answers) return;
    setImporting(true);
    setImportError(null);
    try {
      setImported(await client.importAnswers(answers));
      onChanged();
    } catch (e) {
      setImportError((e as Error).message || t('Something went wrong.'));
    } finally {
      setImporting(false);
    }
  };

  const fileName = file?.name ?? 'list';
  const rows = parsed?.rows ?? [];

  if (minimised) {
    return <UploadPill run={run} total={rows.length} fileName={fileName} onRestore={onRestore} />;
  }

  let header: ReactNode;
  let body: ReactNode;
  let footer: ReactNode;
  if (run) {
    const counts = tally(run.results);
    const waiting = rows.length - counts.done;
    const minutes = minutesLeft(waiting);
    const by = run.batch?.submitted_by_name ? t('Started by {{name}}', { name: run.batch.submitted_by_name }) : null;
    let title = t('Looking up {{file}}', { file: fileName });
    let sub: string;
    if (run.phase === 'done') {
      title = run.cancelled ? t('Stopped {{file}}', { file: fileName }) : t('Looked up {{file}}', { file: fileName });
      sub = [by, t('{{done}} of {{serials}} looked up', { done: counts.done.toLocaleString(), serials: serialCount(rows.length, t) })].filter(Boolean).join(' · ');
    } else if (run.phase === 'finishing') {
      sub = [by, t('finishing')].filter(Boolean).join(' · ');
    } else {
      sub = [by, minutes > 1 ? t('about {{minutes}} minutes left', { minutes }) : t('about a minute left')].filter(Boolean).join(' · ');
    }
    header = (
      <>
        <div className="flex-1">
          <h2 id={headingId} className="m-0 text-lg font-extrabold tracking-[-0.02em]">{title}</h2>
          <div className="mt-0.5 text-[13px] text-[#5C6B85] dark:text-[#8b949e]">{sub}</div>
        </div>
        {run.phase === 'done'
          ? <IconButton label={t('Close')} icon="close" onClick={onClose} />
          : <IconButton label={t('Minimise')} icon="minus" onClick={onMinimise} />}
      </>
    );
    body = <RunBody run={run} rows={rows} />;
    footer = run.phase === 'done' ? (
      <>
        <button type="button" className={buttonClass()} onClick={() => downloadCsv(csvName(fileName, 'results'), resultsCsv(rows, run.results))}>
          <Icon name="download" size={15} />{t('Results (CSV)')}
        </button>
        <button
          type="button"
          className={`${buttonClass()} mr-auto`}
          onClick={() => downloadCsv(csvName(fileName, 'failures'), resultsCsv(rows, run.results, true))}
          disabled={counts.needs_info + counts.error === 0}
        >
          {t('Failures (CSV)')}
        </button>
        <button type="button" className={buttonClass({ primary: true })} onClick={onClose}>{t('Done')}</button>
      </>
    ) : (
      <>
        <button type="button" className={`${buttonClass()} mr-auto`} onClick={() => downloadCsv(csvName(fileName, 'results'), resultsCsv(rows, run.results))}>
          <Icon name="download" size={15} />{t('Results so far (CSV)')}
        </button>
        <button type="button" className={buttonClass({ danger: true })} onClick={() => runRef.current?.abort()} disabled={run.phase !== 'running'}>
          {t('Stop')}
        </button>
      </>
    );
  } else {
    header = (
      <>
        <h2 id={headingId} className="m-0 flex-1 text-lg font-extrabold tracking-[-0.02em]">{t('Upload a list')}</h2>
        <div role="group" aria-label={t('Upload type')} className="inline-flex gap-0.5 rounded-[10px] bg-[#F1F2F5] dark:bg-[#161b22] p-[3px]">
          {(['lookup', 'import'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={mode === value}
              disabled={importing || reading}
              onClick={() => setMode(value)}
              className={`h-[34px] cursor-pointer rounded-lg border-0 px-3.5 text-[13px] font-semibold ${
                mode === value ? 'bg-white dark:bg-[#30363d] text-[#0B1B3D] dark:text-[#e6edf3] shadow-[0_1px_2px_rgba(11,27,61,.08)]' : 'bg-transparent text-[#5C6B85] dark:text-[#8b949e]'
              }`}
            >
              {value === 'lookup' ? t('Look up a list') : t('Import answers')}
            </button>
          ))}
        </div>
        <IconButton label={t('Close')} icon="close" onClick={onClose} disabled={importing || reading} />
      </>
    );
    if (mode === 'lookup') {
      body = (
        <>
          <Stepper steps={[t('Choose file'), t('Review'), t('Look up')]} current={parsed ? 1 : 0} />
          <div className="flex flex-col gap-[18px] px-6 py-5">
            {parsed && file ? (
              <Review file={file} parsed={parsed} onChange={(next) => void read(next)} />
            ) : (
              <DropZone
                label={t('List file')}
                accept=".xlsx,.csv"
                title={t('Drop the customer’s list here, or choose a file')}
                hint={t('Excel or CSV · Column A: OEM · Column B: serial number · other columns ignored')}
                busy={reading ? t('Reading {{file}}…', { file: file?.name ?? '' }) : null}
                disabled={!client || reading}
                onFile={(next) => void read(next)}
              />
            )}
            <ErrorLine message={readError} />
          </div>
        </>
      );
      footer = (
        <>
          <button type="button" className={buttonClass()} onClick={onClose} disabled={reading}>{t('Cancel')}</button>
          {parsed && (
            <button type="button" className={buttonClass({ primary: true })} onClick={() => void start()} disabled={!client || rows.length === 0}>
              {t('Look up {{serials}}', { serials: serialCount(rows.length, t) })}
            </button>
          )}
        </>
      );
    } else {
      body = (
        <>
          <Stepper steps={[t('Choose file'), t('Import')]} current={imported ? 2 : answers ? 1 : 0} />
          <div className="flex flex-col gap-[18px] px-6 py-5">
            {!answers && (
              <DropZone
                label={t('Answers file')}
                accept=".json,.jsonl,.zip"
                title={t('Drop the answers file here, or choose a file')}
                hint={t('The warranty agent’s own answers (.json, .jsonl or .zip), e.g. Dell serials enriched locally')}
                busy={null}
                disabled={!client}
                onFile={(next) => {
                  setAnswers(next);
                  setImported(null);
                  setImportError(null);
                }}
              />
            )}
            {answers && !imported && (
              <>
                <FileRow
                  name={answers.name}
                  hint={t('Each answer is stored like a lookup’s, with the file as its source, in one batch.')}
                  accept=".json,.jsonl,.zip"
                  label={t('Answers file')}
                  disabled={importing}
                  onChange={(next) => {
                    setAnswers(next);
                    setImportError(null);
                  }}
                />
                <Note>{t('Up to 5,000 answers or 25 MB. Nothing is asked of the OEM: each answer is stored as it is.')}</Note>
              </>
            )}
            {answers && imported && <ImportResult file={answers.name} result={imported} />}
            <ErrorLine message={importError} />
          </div>
        </>
      );
      footer = imported ? (
        <>
          <button
            type="button"
            className={buttonClass()}
            onClick={() => {
              setAnswers(null);
              setImported(null);
            }}
          >
            {t('Import another file')}
          </button>
          <button type="button" className={buttonClass({ primary: true })} onClick={onClose}>{t('Done')}</button>
        </>
      ) : (
        <>
          <button type="button" className={buttonClass()} onClick={onClose} disabled={importing}>{t('Cancel')}</button>
          <button type="button" className={buttonClass({ primary: true })} onClick={() => void importFile()} disabled={!client || !answers || importing}>
            {importing ? t('Importing…') : t('Import')}
          </button>
        </>
      );
    }
  }

  return (
    <>
      <div aria-hidden="true" className="fixed inset-0 z-[60] bg-[rgba(11,27,61,0.32)] dark:bg-[rgba(1,4,9,0.72)]" />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="fixed left-1/2 top-14 z-[61] flex max-h-[calc(100vh-80px)] w-[760px] max-w-[calc(100%-32px)] -translate-x-1/2 flex-col rounded-2xl bg-white dark:bg-[#0d1117] text-[#0B1B3D] dark:text-[#e6edf3] shadow-[0_12px_32px_rgba(11,27,61,0.16)] dark:border dark:border-[#30363d] outline-none"
      >
        <div className="flex items-center gap-3 border-b border-[#E5E7EB] dark:border-[#30363d] px-6 pb-4 pt-[22px]">{header}</div>
        <div className="min-h-0 flex-1 overflow-y-auto">{body}</div>
        <div className="flex items-center justify-end gap-2.5 border-t border-[#E5E7EB] dark:border-[#30363d] px-6 py-4">{footer}</div>
      </div>
    </>
  );
}

function IconButton({ label, icon, onClick, disabled }: {
  readonly label: string;
  readonly icon: 'close' | 'minus';
  readonly onClick: () => void;
  readonly disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={disabled}
      className="inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border border-[#E5E7EB] dark:border-[#30363d] bg-white dark:bg-[#0d1117] text-[#434A59] dark:text-[#c9d1d9] hover:bg-[#F7F8FA] dark:hover:bg-[#161b22] disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Icon name={icon} />
    </button>
  );
}

/** Steps before `current` are done; `current` past the last means all are. */
function Stepper({ steps, current }: { readonly steps: string[]; readonly current: number }) {
  const { t } = useTranslation();
  return (
    <ol aria-label={t('Steps')} className="m-0 flex list-none items-center gap-2 border-b border-[#E5E7EB] dark:border-[#30363d] px-6 py-4 text-[13px] font-semibold">
      {steps.map((label, index) => {
        const done = index < current;
        const now = index === current;
        return (
          <Fragment key={label}>
            {index > 0 && <li aria-hidden="true" className="h-px w-8 shrink-0 bg-[#CBD5E1] dark:bg-[#30363d]" />}
            <li aria-current={now ? 'step' : undefined} className={`flex items-center gap-2 ${done ? 'text-[#047857] dark:text-[#34D399]' : now ? 'text-[#0B1B3D] dark:text-[#e6edf3]' : 'text-[#5C6B85] dark:text-[#8b949e]'}`}>
              {done && (
                <span className="inline-flex size-[22px] items-center justify-center rounded-full bg-[#D1FAE5] dark:bg-[#10B981]/15">
                  <Icon name="check" size={12} className={TONE_TEXT.ok} strokeWidth={3} />
                </span>
              )}
              {now && <span className="inline-flex size-[22px] items-center justify-center rounded-full bg-[#0B1B3D] dark:bg-[#e6edf3] text-xs text-white dark:text-[#0d1117]">{index + 1}</span>}
              {!done && !now && (
                <span className="box-border inline-flex size-[22px] items-center justify-center rounded-full border-2 border-solid border-[#CBD5E1] dark:border-[#454c56] text-xs">{index + 1}</span>
              )}
              {label}
              {done && <SrOnly>{t('(done)')}</SrOnly>}
            </li>
          </Fragment>
        );
      })}
    </ol>
  );
}

/** A file input dressed as a drop area: drop a file on it, or choose one. */
function DropZone({ label, accept, title, hint, busy, disabled, onFile }: {
  /** The input's accessible name. */
  readonly label: string;
  readonly accept: string;
  readonly title: string;
  readonly hint: string;
  /** "Reading list.xlsx…" while the file is read. */
  readonly busy: string | null;
  readonly disabled?: boolean;
  readonly onFile: (file: File) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(event) => {
        event.preventDefault();
        if (!disabled) setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        const dropped = event.dataTransfer.files?.[0];
        if (dropped && !disabled) onFile(dropped);
      }}
      className={`flex flex-col items-center gap-2 rounded-xl border-2 border-dashed px-6 py-9 text-center ${over ? 'border-[#0B1B3D] dark:border-[#e6edf3] bg-[#F7F8FA] dark:bg-[#161b22]' : 'border-[#CBD5E1] dark:border-[#454c56] bg-white dark:bg-[#0d1117]'}`}
    >
      <IconTile name="upload" tone="plain" size={40} iconSize={20} />
      <div className="text-sm font-semibold">{title}</div>
      <div className="text-xs text-[#5C6B85] dark:text-[#8b949e]">{hint}</div>
      <input
        ref={inputRef}
        type="file"
        aria-label={label}
        accept={accept}
        className="hidden"
        disabled={disabled}
        onChange={(event) => {
          const chosen = event.target.files?.[0];
          event.target.value = '';
          if (chosen) onFile(chosen);
        }}
      />
      {busy ? (
        <div role="status" className="mt-2 flex items-center gap-2 text-[13px] text-[#5C6B85] dark:text-[#8b949e]"><span className={TEXT}><Ring size={14} /></span>{busy}</div>
      ) : (
        <button type="button" className={`${buttonClass({ small: true })} mt-2`} onClick={() => inputRef.current?.click()} disabled={disabled}>
          {t('Choose file')}
        </button>
      )}
    </div>
  );
}

function FileRow({ name, hint, accept, label, disabled, onChange }: {
  readonly name: string;
  readonly hint: string;
  readonly accept: string;
  readonly label: string;
  readonly disabled?: boolean;
  readonly onChange: (file: File) => void;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center gap-3 rounded-[10px] border border-[#E5E7EB] dark:border-[#30363d] bg-[#F7F8FA] dark:bg-[#161b22] px-3.5 py-3">
      <IconTile name="sheet" tone="ok" size={36} iconSize={18} />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{name}</div>
        <div className="text-xs text-[#5C6B85] dark:text-[#8b949e]">{hint}</div>
      </div>
      <input
        ref={inputRef}
        type="file"
        aria-label={label}
        accept={accept}
        className="hidden"
        disabled={disabled}
        onChange={(event) => {
          const chosen = event.target.files?.[0];
          event.target.value = '';
          if (chosen) onChange(chosen);
        }}
      />
      <button type="button" className={buttonClass({ small: true })} onClick={() => inputRef.current?.click()} disabled={disabled}>
        {t('Change file')}
      </button>
    </div>
  );
}

function Note({ children }: { readonly children: ReactNode }) {
  return (
    <p className="m-0 flex items-start gap-2.5 rounded-[10px] bg-[#EEF1F8] dark:bg-[#161b22] px-3.5 py-3 text-[13px] leading-normal text-[#434A59] dark:text-[#c9d1d9]">
      <span className="mt-0.5"><Icon name="info" className={TEXT} /></span>
      <span>{children}</span>
    </p>
  );
}

const SMALL_TH = 'border-b border-[#E5E7EB] dark:border-[#30363d] bg-[#F1F2F5] dark:bg-[#161b22] px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[.05em] text-[#5C6B85] dark:text-[#8b949e]';
const SMALL_TD = 'border-b border-[#E5E7EB] dark:border-[#30363d] px-3 py-[9px] text-[13px] text-[#0B1B3D] dark:text-[#e6edf3]';

/** The OEMs of some rows, by name, in the order they come. */
function oemNames(rows: readonly BulkRow[]): { names: string[]; others: boolean } {
  const names: string[] = [];
  let others = false;
  for (const row of rows) {
    const name = row.oem ? OEM_NAMES[row.oem] ?? row.oem_text ?? row.oem : null;
    if (!name) others = true;
    else if (!names.includes(name)) names.push(name);
  }
  return { names: names.sort((a, b) => a.localeCompare(b)), others };
}

/** DOC 4: what the service read from the file, before anything is looked up. */
function Review({ file, parsed, onChange }: { readonly file: File; readonly parsed: BulkRows; readonly onChange: (file: File) => void }) {
  const { t } = useTranslation();
  const [showInvalid, setShowInvalid] = useState(false);
  const looked = oemNames(parsed.rows);
  const lookedText = looked.names.length === 0
    ? t('Serials of any OEM.')
    : looked.names.length > 2 || looked.others
      ? t('{{names}} and other OEMs.', { names: looked.names.slice(0, 2).join(', ') })
      : t('{{names}}.', { names: looked.names.join(' and ') });
  const blockedNames = oemNames(parsed.blocked).names.join(', ') || OEM_NAMES.dell;
  const perMinute = Math.round((60 / SECONDS_PER_LOOKUP) * BULK_CONCURRENCY);

  return (
    <>
      <FileRow
        name={file.name}
        hint={t('Column A: OEM · Column B: serial number · other columns ignored')}
        accept=".xlsx,.csv"
        label={t('List file')}
        onChange={onChange}
      />
      <div role="group" aria-label={t('Rows in the file')} className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div role="group" aria-label={t('To look up')} className="flex flex-col gap-1.5 rounded-xl border border-[#0B1B3D] dark:border-[#e6edf3] bg-white dark:bg-[#0d1117] p-4 shadow-[inset_0_0_0_1px_#0B1B3D] dark:shadow-[inset_0_0_0_1px_#e6edf3]">
          <div className={LABEL}>{t('To look up')}</div>
          <div className="text-[26px] font-extrabold">{parsed.rows.length.toLocaleString()}</div>
          <div className="text-xs leading-[1.45] text-[#5C6B85] dark:text-[#8b949e]">{lookedText} {t('Serials already stored come back at once.')}</div>
        </div>
        <div role="group" aria-label={t('{{oem}} · enrich locally', { oem: blockedNames })} className="flex flex-col gap-1.5 rounded-xl border border-[#E5E7EB] dark:border-[#30363d] bg-white dark:bg-[#0d1117] p-4">
          <div className={LABEL}>{t('{{oem}} · enrich locally', { oem: blockedNames })}</div>
          <div className="text-[26px] font-extrabold">{parsed.blocked.length.toLocaleString()}</div>
          <div className="text-xs leading-[1.45] text-[#5C6B85] dark:text-[#8b949e]">
            {t('{{oem}}’s site blocks the agent. Send these to be enriched, then import the answers.', { oem: blockedNames })}
          </div>
          {parsed.blocked.length > 0 && (
            <button type="button" className={`${LINK} mt-1`} onClick={() => downloadCsv(csvName(file.name, 'blocked'), blockedCsv(parsed.blocked))}>
              {t('Download {{oem}} rows (CSV)', { oem: blockedNames })}
            </button>
          )}
        </div>
        <div role="group" aria-label={t('Can’t use')} className="flex flex-col gap-1.5 rounded-xl border border-[#E5E7EB] dark:border-[#30363d] bg-white dark:bg-[#0d1117] p-4">
          <div className={LABEL}>{t('Can’t use')}</div>
          <div className="text-[26px] font-extrabold">{parsed.invalid.length.toLocaleString()}</div>
          <div className="text-xs leading-[1.45] text-[#5C6B85] dark:text-[#8b949e]">{t('Not a serial number, or a duplicate row.')}</div>
          {parsed.invalid.length > 0 && (
            <button type="button" aria-expanded={showInvalid} className={`${LINK} mt-1`} onClick={() => setShowInvalid((shown) => !shown)}>
              {showInvalid ? t('Hide these rows') : t('See which rows')}
            </button>
          )}
        </div>
      </div>

      {showInvalid && (
        <div className="overflow-hidden rounded-[10px] border border-[#E5E7EB] dark:border-[#30363d]">
          <table aria-label={t('Rows that can’t be used')} className="w-full border-collapse">
            <thead>
              <tr>
                <th className={`${SMALL_TH} w-20`}>{t('Row')}</th>
                <th className={SMALL_TH}>{t('OEM')}</th>
                <th className={SMALL_TH}>{t('Serial number')}</th>
                <th className={SMALL_TH}>{t('Why')}</th>
              </tr>
            </thead>
            <tbody>
              {parsed.invalid.slice(0, INVALID_SHOWN).map((row) => (
                <tr key={row.row}>
                  <td className={SMALL_TD}>{row.row}</td>
                  <td className={SMALL_TD}>{row.oem_text || '—'}</td>
                  <td className={`${SMALL_TD} ${MONO}`}>{row.serial_number || '—'}</td>
                  <td className={`${SMALL_TD} text-[#5C6B85] dark:text-[#8b949e]`}>{row.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {parsed.invalid.length > INVALID_SHOWN && (
            <p className="m-0 px-3 py-2 text-xs text-[#5C6B85] dark:text-[#8b949e]">{t('Showing {{shown}} of {{total}} rows.', { shown: INVALID_SHOWN, total: parsed.invalid.length })}</p>
          )}
        </div>
      )}

      {parsed.rows.length > 0 && (
        <div>
          <div className="mb-2 flex items-baseline justify-between">
            <h3 className="m-0 text-sm font-bold">{t('First rows to look up')}</h3>
            <span className="text-xs text-[#5C6B85] dark:text-[#8b949e]">{t('as read from the file')}</span>
          </div>
          <div className="overflow-hidden rounded-[10px] border border-[#E5E7EB] dark:border-[#30363d]">
            <table aria-label={t('First rows to look up')} className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={`${SMALL_TH} w-20`}>{t('Row')}</th>
                  <th className={SMALL_TH}>{t('OEM')}</th>
                  <th className={SMALL_TH}>{t('Serial number')}</th>
                </tr>
              </thead>
              <tbody>
                {parsed.rows.slice(0, FIRST_ROWS).map((row, index, shown) => {
                  const last = index === shown.length - 1 ? ' border-b-0' : '';
                  return (
                    <tr key={row.row}>
                      <td className={`${SMALL_TD}${last}`}>{row.row}</td>
                      <td className={`${SMALL_TD}${last}`}>{(row.oem && OEM_NAMES[row.oem]) || row.oem_text || '—'}</td>
                      <td className={`${SMALL_TD} ${MONO}${last}`}>{row.serial_number}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Note>
        {t('New serials take about {{seconds}} seconds each, {{concurrency}} at a time: roughly {{perMinute}} a minute. Keep this tab open until it finishes; stopping keeps what was already found.', {
          seconds: SECONDS_PER_LOOKUP,
          concurrency: BULK_CONCURRENCY,
          perMinute,
        })}
      </Note>
    </>
  );
}

/** DOC 5: the run's progress, its counts and the latest rows. */
function RunBody({ run, rows }: { readonly run: Run; readonly rows: readonly BulkRow[] }) {
  const { t } = useTranslation();
  const counts = tally(run.results);
  const total = rows.length;
  const waiting = total - counts.done;
  const percent = total ? Math.round((counts.done / total) * 100) : 0;
  const share = (count: number) => `${total ? (count / total) * 100 : 0}%`;
  const latest = Array.from({ length: Math.min(LATEST_ROWS, run.started) }, (_, i) => run.started - 1 - i);
  const running = run.phase !== 'done';

  return (
    <div className="flex flex-col gap-[18px] px-6 py-5">
      <div>
        <div className="mb-2 flex justify-between text-[13px]">
          <strong aria-live="polite">{t('{{done}} of {{serials}}', { done: counts.done.toLocaleString(), serials: serialCount(total, t) })}</strong>
          <span className="text-[#5C6B85] dark:text-[#8b949e]">{t('{{count}} at a time', { count: BULK_CONCURRENCY })}</span>
        </div>
        <div
          role="progressbar"
          aria-label={t('Progress')}
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          className="flex h-2 overflow-hidden rounded-full bg-[#F1F2F5] dark:bg-[#21262d]"
        >
          <div className="bg-[#10B981]" style={{ width: share(counts.found) }} />
          <div className="bg-[#D97706]" style={{ width: share(counts.needs_info) }} />
          <div className="bg-[#DC2626]" style={{ width: share(counts.error) }} />
          <div className="bg-[#0B1B3D] dark:bg-[#e6edf3]" style={{ width: share(counts.not_found) }} />
        </div>
      </div>
      <div role="group" aria-label={t('Counts')} className="grid grid-cols-2 gap-2.5 sm:grid-cols-5">
        <Count value={counts.found} label={t('Found')} colour={TONE_TEXT.ok} />
        <Count value={counts.not_found} label={t('Not found by OEM')} colour={TEXT} />
        <Count value={counts.needs_info} label={t('Needs model no.')} colour={TONE_TEXT.warn} />
        <Count value={counts.error} label={t('Failed')} colour={TONE_TEXT.bad} />
        <Count value={waiting} label={running ? t('Waiting') : t('Skipped')} colour={MUTED} />
      </div>
      {latest.length > 0 && (
        <div>
          <h3 className="m-0 mb-2 text-sm font-bold">{t('Latest')}</h3>
          <div className="overflow-hidden rounded-[10px] border border-[#E5E7EB] dark:border-[#30363d]">
            <table className="w-full border-collapse">
              <caption className="sr-only">{t('Latest rows')}</caption>
              <tbody>
                {latest.map((index, position) => (
                  <LatestRow key={rows[index].row} row={rows[index]} result={run.results[index]} running={running} last={position === latest.length - 1} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {run.finishError && <ErrorLine message={run.finishError} />}
      <p className="m-0 text-[13px] text-[#5C6B85] dark:text-[#8b949e]">
        {running
          ? t('Rows that need a model number or failed can be run again from the results file once this finishes.')
          : t('Rows that need a model number or failed are in the failures file: correct them and upload it as a new list.')}
      </p>
    </div>
  );
}

/** A number over its label; `colour` is its text colour classes (light and dark). */
function Count({ value, label, colour }: { readonly value: number; readonly label: string; readonly colour: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-[10px] border border-[#E5E7EB] dark:border-[#30363d] p-3">
      <b className={`text-[22px] font-extrabold ${colour}`}>{value.toLocaleString()}</b>
      <span className="text-xs text-[#5C6B85] dark:text-[#8b949e]">{label}</span>
    </div>
  );
}

const LATEST_TD = 'border-b border-[#E5E7EB] dark:border-[#30363d] px-3 py-2.5 align-middle text-[13px] text-[#0B1B3D] dark:text-[#e6edf3]';

function LatestRow({ row, result, running, last }: {
  readonly row: BulkRow;
  readonly result: RowResult | null;
  readonly running: boolean;
  readonly last: boolean;
}) {
  const { t } = useTranslation();
  const td = `${LATEST_TD}${last ? ' border-b-0' : ''}`;
  const answer = result?.answer;
  const oem = oemName(answer?.oem) ?? oemName(row.oem) ?? row.oem_text ?? null;
  let detail: string = oem ?? '';
  let muted = true;
  let badge: ReactNode;
  if (!result) {
    badge = running
      ? <Badge tone="info"><Ring />{t('Checking')}</Badge>
      : <Badge tone="neutral">{t('Skipped')}</Badge>;
    if (!running) detail = t('not looked up');
  } else {
    const shown = OUTCOME_BADGES[result.outcome];
    badge = <Badge tone={shown.tone}>{t(shown.label)}</Badge>;
    if (result.outcome === 'found') {
      const warranty = answer?.warranty;
      if (answer?.served_from === 'cache') detail = t('already stored');
      else {
        muted = false;
        if (warranty?.status === 'active' && warranty.end_date) detail = t('{{oem}} · until {{date}}', { oem: oem ?? '', date: warranty.end_date });
        else if (warranty?.status === 'expired' && warranty.end_date) detail = t('{{oem}} · ended {{date}}', { oem: oem ?? '', date: warranty.end_date });
      }
    } else if (result.outcome === 'not_found') {
      detail = t('{{oem}} does not know it', { oem: oem ?? t('The OEM') });
    } else if (result.outcome === 'needs_info') {
      detail = t('{{oem}} asks for the model number', { oem: oem ?? t('The OEM') });
    } else {
      detail = result.error || t('OEM site did not answer');
    }
  }
  return (
    <tr>
      <td className={`${td} w-14 whitespace-nowrap text-[#5C6B85] dark:text-[#8b949e]`}>{t('Row {{row}}', { row: row.row })}</td>
      <td className={`${td} ${MONO}`}>{row.serial_number}</td>
      <td className={`${td} ${muted ? 'text-[#5C6B85] dark:text-[#8b949e]' : ''}`}>{detail}</td>
      <td className={`${td} text-right`}>{badge}</td>
    </tr>
  );
}

function ImportResult({ file, result }: { readonly file: string; readonly result: ImportResponse }) {
  const { t } = useTranslation();
  const results = result.results ?? [];
  const counts = result.counts ?? {};
  return (
    <>
      <p role="status" className="m-0 text-sm">
        {t('Imported {{file}}: {{counts}}.', { file, counts: countsText(counts, t) || t('nothing stored') })}
      </p>
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-5">
        <Count value={counts.stored ?? 0} label={t('Stored')} colour={TEXT} />
        <Count value={counts.found ?? 0} label={t('Found')} colour={TONE_TEXT.ok} />
        <Count value={counts.not_found ?? 0} label={t('Not found by OEM')} colour={TEXT} />
        <Count value={counts.skipped ?? 0} label={t('Skipped')} colour={MUTED} />
        <Count value={counts.failed ?? 0} label={t('Failed')} colour={TONE_TEXT.bad} />
      </div>
      {results.length > 0 && (
        <div className="overflow-hidden rounded-[10px] border border-[#E5E7EB] dark:border-[#30363d]">
          <table aria-label={t('Imported answers')} className="w-full border-collapse">
            <thead>
              <tr>
                <th className={`${SMALL_TH} w-14`}>#</th>
                <th className={SMALL_TH}>{t('Serial number')}</th>
                <th className={SMALL_TH}>{t('Result')}</th>
                <th className={`${SMALL_TH} !text-right`}>{t('Coverages')}</th>
                <th className={SMALL_TH}>{t('Reason')}</th>
              </tr>
            </thead>
            <tbody>
              {results.slice(0, IMPORT_RESULTS_SHOWN).map((item) => {
                const badge = IMPORT_BADGES[item.result] ?? { label: item.result, tone: 'neutral' as const };
                return (
                  <tr key={item.index}>
                    <td className={`${SMALL_TD} text-[#5C6B85] dark:text-[#8b949e]`}>{item.index + 1}</td>
                    <td className={`${SMALL_TD} ${MONO}`}>{item.serial_number || '—'}</td>
                    <td className={SMALL_TD}><Badge tone={badge.tone}>{t(badge.label)}</Badge></td>
                    <td className={`${SMALL_TD} text-right`}>{item.coverages ?? '—'}</td>
                    <td className={`${SMALL_TD} text-[#5C6B85] dark:text-[#8b949e]`}>{item.reason ?? ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {results.length > IMPORT_RESULTS_SHOWN && (
            <p className="m-0 px-3 py-2 text-xs text-[#5C6B85] dark:text-[#8b949e]">{t('Showing {{shown}} of {{total}} answers.', { shown: IMPORT_RESULTS_SHOWN, total: results.length })}</p>
          )}
        </div>
      )}
    </>
  );
}

/** The minimised run, bottom right; it opens the dialog again. */
function UploadPill({ run, total, fileName, onRestore }: {
  readonly run: Run | null;
  readonly total: number;
  readonly fileName: string;
  readonly onRestore: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  const done = run ? tally(run.results).done : 0;
  const finished = run?.phase === 'done';
  return (
    <>
      <SrOnly role="status">{finished ? t('Finished looking up {{file}}', { file: fileName }) : ''}</SrOnly>
      <button
        ref={ref}
        type="button"
        onClick={onRestore}
        className="fixed bottom-6 right-6 z-[55] inline-flex h-11 cursor-pointer items-center gap-3 rounded-full border border-[#E5E7EB] dark:border-[#30363d] bg-white dark:bg-[#0d1117] px-4 text-[13px] font-semibold text-[#0B1B3D] dark:text-[#e6edf3] shadow-[0_8px_24px_rgba(11,27,61,0.16)] hover:bg-[#F7F8FA] dark:hover:bg-[#161b22]"
      >
        {finished ? <Icon name="check" size={14} className={TONE_TEXT.ok} strokeWidth={3} /> : <Ring size={14} />}
        <span>{finished ? t('Looked up {{file}}', { file: fileName }) : t('Looking up {{file}}', { file: fileName })}</span>{' '}
        <span className="font-normal text-[#5C6B85] dark:text-[#8b949e]">{t('{{done}} of {{total}}', { done, total })}</span>
        <span aria-hidden="true" className="h-1.5 w-16 overflow-hidden rounded-full bg-[#F1F2F5] dark:bg-[#21262d]">
          <span className="block h-full bg-[#10B981]" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
        </span>
      </button>
    </>
  );
}
